import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("dark", "luna");

type Locale = "ko" | "en" | "ja";
type Row = [string, string, string, string];

const LOCALE_STORAGE_KEY = "lunatarot-privacy-locale";
const locales: Locale[] = ["ko", "en", "ja"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN", ja: "JA" };

const tableRows: Record<Locale, Row[]> = {
  ko: [
    ["로컬 리딩 데이터", "타로 리딩 기록, 스프레드 선택 정보, 앱 내 설정값", "리딩 히스토리 조회, 사용자 경험 개선", "사용자 기기 내 (앱 로컬 저장소)"],
    ["Firebase Remote Config / Crashlytics", "앱 버전, 충돌 로그, 기기 정보(익명)", "앱 안정성 개선, 오류 분석 및 기능 업데이트 관리", "Firebase 서버 (Google LLC)"],
    ["광고 SDK (Google AdMob)", "광고 식별자, 언어/국가 정보, 디바이스 정보(익명)", "광고 제공, 광고 성과 분석 및 서비스 유지", "Google AdMob 서버"],
    ["AI 리딩 분석", "질문 또는 메모, 선택한 카드/스프레드 정보, 최소한의 앱 컨텍스트", "AI 기반 타로 해석 및 리딩 분석 결과 생성", "Google Gemini API 관련 서버"],
  ],
  en: [
    ["Local reading data", "Tarot reading history, spread selection, in-app settings", "History access and experience improvement", "On device (local app storage)"],
    ["Firebase Remote Config / Crashlytics", "App version, crash logs, anonymous device information", "Stability improvement, error analysis, and feature rollout management", "Firebase servers (Google LLC)"],
    ["Ad SDK (Google AdMob)", "Advertising identifier, language/country, anonymous device information", "Ad delivery, performance analytics, and service support", "Google AdMob servers"],
    ["AI reading analysis", "Question or memo, selected cards/spread information, minimal app context", "Generate AI-based tarot interpretation and analysis", "Google Gemini API related servers"],
  ],
  ja: [
    ["ローカルリーディングデータ", "タロット履歴、スプレッド選択情報、アプリ内設定", "履歴表示と体験改善", "ユーザー端末内（ローカル保存）"],
    ["Firebase Remote Config / Crashlytics", "アプリバージョン、クラッシュログ、匿名の端末情報", "安定性改善、エラー分析、機能更新管理", "Firebase サーバー（Google LLC）"],
    ["広告 SDK (Google AdMob)", "広告識別子、言語/国情報、匿名の端末情報", "広告配信、成果分析、サービス維持", "Google AdMob サーバー"],
    ["AI リーディング分析", "質問やメモ、選択したカード/スプレッド情報、最小限のアプリ文脈", "AI によるタロット解釈と分析結果の生成", "Google Gemini API 関連サーバー"],
  ],
};

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    tableHead: Row;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
    websiteStats: { title: string; body: string; list: string[] };
  }
> = {
  ko: {
    crumb: "LunaTarot Privacy",
    title: "LunaTarot,",
    accentTitle: "개인정보 처리방침",
    subtitle:
      "최종 업데이트일: 2026년 4월 13일. 본 정책의 운영 주체는 Daydream Factory이며, LunaTarot는 회원가입 없이 사용할 수 있고 리딩 기록은 기기 안에 저장됩니다. AI 분석 기능을 사용할 때만 질문/메모와 카드 정보가 외부 API로 전송될 수 있습니다.",
    tableHead: ["구분", "수집 항목", "사용 목적", "저장 위치"],
    sections: [
      { title: "1. 수집하는 정보" },
      {
        title: "2. 데이터 사용 목적",
        list: [
          "타로 리딩 기록 및 히스토리 기능 제공",
          "사용자가 요청한 AI 리딩 분석 결과 생성",
          "앱 오류 및 성능 개선을 위한 통계 및 로그 분석",
          "서비스 업데이트 및 기능 제어를 위한 원격 설정 관리",
          "광고 표시 및 서비스 유지에 필요한 통계 분석",
        ],
      },
      {
        title: "3. 제3자 서비스",
        body: "아래 서비스 제공자가 익명 수준의 운영 데이터 또는 사용자가 요청한 분석 데이터를 처리할 수 있습니다.",
        list: ["Firebase (Google LLC)", "Google AdMob (Google LLC)", "Google Gemini API (Google LLC)"],
      },
      {
        title: "4. 데이터 보관 및 삭제",
        list: [
          "로컬 데이터는 사용자 기기 내에만 저장되며 앱 삭제 시 함께 제거됩니다.",
          "Firebase 및 AdMob 로그·통계 정보는 각 서비스 제공자 정책에 따라 자동 삭제됩니다.",
          "AI 분석 요청 데이터의 처리 및 보관은 Google Gemini API 정책에 따를 수 있습니다.",
        ],
      },
      {
        title: "5. 사용자 권리 및 보안",
        list: [
          "앱을 삭제하면 모든 로컬 데이터가 함께 삭제됩니다.",
          "AI 분석은 선택 기능이며, 민감한 개인정보 입력은 피하는 것을 권장합니다.",
          "앱은 HTTPS 통신과 합리적인 접근 제한을 통해 데이터를 보호합니다.",
          "문의: support@daydreamfactory.net",
        ],
      },
    ],
    websiteStats: {
      title: "6. 웹사이트 방문 통계",
      body:
        "Daydream Factory 웹사이트 및 LunaTarot 랜딩 페이지에서는 방문 흐름과 페이지 이용 현황을 익명 수준으로 확인하기 위해 Firebase Analytics (Google LLC)를 사용할 수 있습니다.",
      list: [
        "수집 예시: 페이지 경로, 방문 시각, 브라우저/기기 유형, 대략적 지역 정보",
        "사용 목적: 랜딩 페이지 개선, 유입 경로 확인, 스토어 버튼 전환 분석",
        "해당 통계는 웹사이트 방문 시에만 적용되며, 앱 내 로컬 리딩 기록과는 분리됩니다.",
      ],
    },
  },
  en: {
    crumb: "LunaTarot Privacy",
    title: "LunaTarot,",
    accentTitle: "Privacy Policy",
    subtitle:
      "Last updated: April 13, 2026. This policy is operated by Daydream Factory. LunaTarot can be used without registration, and reading history is stored on the device. Only when AI analysis is used may your question or memo and card information be sent to an external API.",
    tableHead: ["Category", "Collected data", "Purpose", "Storage"],
    sections: [
      { title: "1. Information We Collect" },
      {
        title: "2. How We Use Data",
        list: [
          "To provide tarot reading history and journal features",
          "To generate AI reading analysis requested by the user",
          "To analyze logs and statistics for stability and performance improvement",
          "To manage remote configuration and feature controls",
          "To support ads and service continuity with necessary analytics",
        ],
      },
      {
        title: "3. Third-Party Services",
        body: "The following providers may process anonymous operational data or analysis input explicitly requested by the user.",
        list: ["Firebase (Google LLC)", "Google AdMob (Google LLC)", "Google Gemini API (Google LLC)"],
      },
      {
        title: "4. Data Retention and Deletion",
        list: [
          "Local data stays on the user's device and is removed when the app is deleted.",
          "Firebase and AdMob logs and statistics are deleted according to each provider's retention policies.",
          "AI analysis request handling and retention may follow Google Gemini API policies.",
        ],
      },
      {
        title: "5. User Rights and Security",
        list: [
          "Deleting the app removes local data stored with it.",
          "AI analysis is optional, and sensitive personal information should not be entered.",
          "The app protects data through HTTPS communication and reasonable access controls.",
          "Contact: support@daydreamfactory.net",
        ],
      },
    ],
    websiteStats: {
      title: "6. Website Visit Analytics",
      body:
        "The Daydream Factory website and LunaTarot landing page may use Firebase Analytics (Google LLC) to understand visit flow and page usage at an anonymous level.",
      list: [
        "Examples: page path, visit time, browser/device type, and approximate region",
        "Purpose: improve landing pages, understand acquisition flow, and measure store button conversion",
        "These statistics apply only to website visits and are separate from in-app local reading history.",
      ],
    },
  },
  ja: {
    crumb: "LunaTarot Privacy",
    title: "LunaTarot,",
    accentTitle: "プライバシーポリシー",
    subtitle:
      "最終更新日: 2026年4月13日。本ポリシーの運営主体は Daydream Factory です。LunaTarot は会員登録なしで利用でき、リーディング履歴は端末内に保存されます。AI 分析機能を使う場合に限り、質問やメモ、カード情報が外部 API に送信されることがあります。",
    tableHead: ["区分", "取得項目", "利用目的", "保存場所"],
    sections: [
      { title: "1. 収集する情報" },
      {
        title: "2. データの利用目的",
        list: [
          "タロット履歴やジャーナル機能の提供",
          "ユーザーが求めた AI リーディング分析の生成",
          "安定性や性能向上のためのログ・統計分析",
          "リモート設定と機能制御の管理",
          "広告表示とサービス維持に必要な統計利用",
        ],
      },
      {
        title: "3. 第三者サービス",
        body: "以下の提供者は、匿名レベルの運用データまたはユーザーが明示的に求めた分析入力を処理することがあります。",
        list: ["Firebase (Google LLC)", "Google AdMob (Google LLC)", "Google Gemini API (Google LLC)"],
      },
      {
        title: "4. データ保管と削除",
        list: [
          "ローカルデータは端末内にのみ保存され、アプリ削除時に一緒に削除されます。",
          "Firebase と AdMob のログ・統計情報は各提供者の方針に従って削除されます。",
          "AI 分析リクエストの処理や保管は Google Gemini API の方針に従う場合があります。",
        ],
      },
      {
        title: "5. ユーザーの権利と安全性",
        list: [
          "アプリを削除するとローカルデータも一緒に削除されます。",
          "AI 分析は任意機能であり、機微な個人情報の入力は避けてください。",
          "アプリは HTTPS 通信と合理的なアクセス制御でデータを保護します。",
          "お問い合わせ: support@daydreamfactory.net",
        ],
      },
    ],
    websiteStats: {
      title: "6. ウェブサイト訪問分析",
      body:
        "Daydream Factory のウェブサイトおよび LunaTarot ランディングページでは、訪問の流れやページ利用状況を匿名レベルで把握するために Firebase Analytics (Google LLC) を使用する場合があります。",
      list: [
        "例: ページパス、訪問時刻、ブラウザ/端末種別、おおよその地域情報",
        "目的: ランディング改善、流入経路の把握、ストアボタンの転換分析",
        "これらの統計はウェブサイト訪問時のみ適用され、アプリ内のローカル履歴とは分離されます。",
      ],
    },
  },
};

const LunaTarotPrivacyPolicy: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "en"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.sections[0].title}</h2>
          <div style={styles.sectionCard}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {copy.tableHead.map((item) => (
                      <th key={item} style={styles.thtd}>
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows[locale].map((row) => (
                    <tr key={row[0]}>
                      {row.map((cell) => (
                        <td key={cell} style={styles.thtd}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {copy.sections.slice(1).map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? <p style={styles.body}>{section.body}</p> : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>
                      {item.includes("support@daydreamfactory.net") ? (
                        <>
                          {item.replace("support@daydreamfactory.net", "")}
                          <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                            support@daydreamfactory.net
                          </a>
                        </>
                      ) : item === "Firebase (Google LLC)" ? (
                        <>
                          Firebase (Google LLC) -{" "}
                          <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer" style={styles.link}>
                            Firebase Privacy &amp; Security
                          </a>
                        </>
                      ) : item === "Google AdMob (Google LLC)" ? (
                        <>
                          Google AdMob (Google LLC) -{" "}
                          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={styles.link}>
                            Google Privacy Policy
                          </a>
                        </>
                      ) : item === "Google Gemini API (Google LLC)" ? (
                        <>
                          Google Gemini API (Google LLC) -{" "}
                          <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noreferrer" style={styles.link}>
                            Gemini API Terms
                          </a>
                        </>
                      ) : (
                        item
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.websiteStats.title}</h2>
          <div style={styles.sectionCard}>
            <p style={styles.body}>{copy.websiteStats.body}</p>
            <ul style={styles.list}>
              {copy.websiteStats.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </main>
  );
};

export default LunaTarotPrivacyPolicy;
